import { DataTable } from '@/components/DataTable';
import { ErrorBoundaryFallbackRender } from '@/components/ui/ErrorBoundaryFallback';
import config from '@/config';
import {
  Device,
  DeviceAggregate,
  DeviceBoolExp,
  DeviceOrderBy,
  OrderBy,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import useDataTable from '@/hooks/useDataTable';
import { useSyncFiltersToSearchParams } from '@/hooks/useSyncFiltersToSearchParams';
import { FeatureName } from '@/models/role.models';
import { SearchParam } from '@/models/search-param.models';
import hasPermission from '@/utils/permission.function';
import { searchParamToTableSortingState } from '@/utils/search-param.function';
import { toSqlLikeString } from '@/utils/string';
import { useQuery } from '@tanstack/react-query';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSearchParams } from 'react-router';
import { validate } from 'uuid';
import { deviceColumns } from '../components/DeviceColumns';
import ListDeviceHeader, { DeviceFilter } from '../components/ListDeviceHeader';
import {
  deviceWithZoneQuery,
  deviceWithZoneQueryNoApiKeyDescription,
  deviceWithoutZoneQuery,
} from '../graphql/query';

type DeviceQueryFilterInput = {
  where: DeviceBoolExp;
  limit: number;
  offset: number;
  orderBy: DeviceOrderBy[];
};
const DEFAULT_COLUMN_VISIBILITY = {
  serial: false,
  product: false,
  environment: false,
  zone: false,
  siteLocation: false,
  users: false,
};

export default function ListDevicePage() {
  const DEFAULT_PAGE_SIZE = 50;
  const DEFAULT_DATA_ORDER: DeviceOrderBy[] = [
    {
      createdAt: OrderBy.Desc,
    },
  ];
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [filterFormsData, setFilterFormsData] = useState<DeviceFilter>();

  const [filters, setFilters] = useState<DeviceQueryFilterInput>({
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
    orderBy: DEFAULT_DATA_ORDER,
    where: {},
  });

  const [searchParams, _setSearchParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>([]);

  const [devices, setDevices] = useState<Device[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [rowCount, setRowCount] = useState<number>(0);
  const [selectedTimezone, setSelectedTimezone] = useState<string>();
  // This state indicate when if the search params are loaded after the page load.
  const [searchParamsLoaded, setSearchParamsLoaded] = useState<boolean>(false);

  useSyncFiltersToSearchParams<DeviceFilter>({
    filters: filterFormsData,
    pagination,
    sorting,
    setPagination,
    searchParamsLoaded,
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });
  const { data, refetch, isFetching, isLoading } = useQuery({
    queryKey: ['Devices', filters],
    queryFn: () =>
      execute({
        query: hasPermission(FeatureName.VIEW_ROI_ZONE)
          ? config.apiKey.hasDescription
            ? deviceWithZoneQuery
            : deviceWithZoneQueryNoApiKeyDescription
          : deviceWithoutZoneQuery,
        variables: {
          limit: filters.limit,
          offset: filters.offset,
          where: filters.where,
          orderBy: filters.orderBy,
        },
      }),
    select: (data) => data,
    staleTime: 3000,
    enabled: searchParamsLoaded,
  });

  useEffect(() => {
    if (data) {
      setDevices((data.device ?? []) as Device[]);
      setRowCount(
        ((data.deviceAggregate as DeviceAggregate)?.aggregate
          ?.count as number) ?? 0,
      );
    }
  }, [data]);

  const table = useDataTable<Device>({
    data: devices,
    columns: deviceColumns,
    rowCount,
    sorting,
    pagination,
    setSorting,
    setPagination,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    defaultColumnVisibilityState: DEFAULT_COLUMN_VISIBILITY,
    extraData: {
      timezone: selectedTimezone,
    },
  });

  useEffect(() => {
    const name = searchParams.get(SearchParam.NAME);
    const site = searchParams.get(SearchParam.SITE);
    const serial = searchParams.get(SearchParam.SERIAL);
    const customer = searchParams.get(SearchParam.CUSTOMER);
    const status = searchParams.get(SearchParam.STATUS);
    const product = searchParams.get(SearchParam.PRODUCT);
    const environment = searchParams.get(SearchParam.ENVIRONMENT);
    const viewMode = searchParams.get(SearchParam.VIEW_MODE) ?? 'list';
    setViewMode(viewMode === 'grid' ? 'grid' : 'list');
    const pageSize = parseInt(
      searchParams.get(SearchParam.PAGE_SIZE) ?? DEFAULT_PAGE_SIZE.toString(),
    );
    const pageIndex = parseInt(searchParams.get(SearchParam.PAGE) ?? '0');
    const orderBy = searchParams.get(SearchParam.ORDER_BY);
    const limit = pageSize;
    const offset = pageIndex * limit;
    const sortingState = searchParamToTableSortingState(orderBy);
    const statusList = status ? status.split(',') : [];

    // Populate form fields, table sorting, and pagination using values from the search parameters.
    if (!searchParamsLoaded) {
      setFilterFormsData({
        name,
        serial,
        site,
        customer,
        environment,
        status: statusList?.length > 0 ? statusList : null,
      });
      setSorting(sortingState);
      setPagination({
        pageIndex,
        pageSize,
      });
    }

    // Create Device Where Filter from SearchParams.
    const andFilters: DeviceBoolExp[] = [];
    if (name) {
      if (validate(name)) {
        andFilters.push({
          id: {
            _eq: name,
          },
        });
      } else {
        andFilters.push({
          name: {
            _ilike: toSqlLikeString(name),
          },
        });
      }
    }

    if (product) {
      const productList = product.split(',');
      andFilters.push({
        products: {
          _contains: productList,
        },
      });
    }

    if (site) {
      andFilters.push({
        site: {
          name: {
            _ilike: toSqlLikeString(site),
          },
        },
      });
    }
    if (serial) {
      andFilters.push({
        serial: {
          _ilike: toSqlLikeString(serial),
        },
      });
    }
    if (customer) {
      andFilters.push({
        site: {
          customer: {
            id: {
              _eq: customer,
            },
          },
        },
      });
    }

    if (environment) {
      andFilters.push({
        config: {
          _contains: {
            state: {
              productionReady: environment === 'production' ? true : false,
            },
          },
        },
      });
    }
    if (statusList.length > 0) {
      // Apply specific filtering logic: if the only selected status is "created", include devices with `state.created = true`
      // and explicitly exclude any where `state.installed` or `state.installationFailed` are either true or false (i.e., those keys must be absent);
      //  otherwise, include devices that have any of the selected statuses set to true in `state`.
      if (statusList.length === 1 && statusList[0] === 'created') {
        andFilters.push({
          config: {
            _contains: {
              state: { created: true },
            },
          },
        });
        andFilters.push({
          _and: [
            {
              _not: {
                config: {
                  _contains: {
                    state: {
                      installed: true,
                    },
                  },
                },
              },
            },
            {
              _not: {
                config: {
                  _contains: {
                    state: {
                      installed: false,
                    },
                  },
                },
              },
            },
            {
              _not: {
                config: {
                  _contains: {
                    state: {
                      installationFailed: true,
                    },
                  },
                },
              },
            },
            {
              _not: {
                config: {
                  _contains: {
                    state: {
                      installationFailed: false,
                    },
                  },
                },
              },
            },
          ],
        });
      } else {
        const orFilter: DeviceBoolExp[] = [];
        statusList.forEach((status) => {
          orFilter.push({
            config: {
              _contains: {
                state: { [status]: true },
              },
            },
          });
        });
        andFilters.push({
          _or: orFilter,
        });
      }
    }
    // Convert the react table SortingState to Device Query OrderBy
    const orderByFromParams = sortingState.map((sort): DeviceOrderBy => {
      const order = sort.desc ? OrderBy.DescNullsLast : OrderBy.AscNullsLast;
      if (sort.id === 'site') {
        return {
          site: {
            name: order,
          },
        };
      }
      if (sort.id === 'customer') {
        return {
          site: {
            customer: {
              name: order,
            },
          },
        };
      }
      if (sort.id === 'lastActive') {
        return {
          lastMedia: {
            lastOriginalMediaTime: order,
          },
        };
      }
      if (sort.id === 'product') {
        return {
          products: order,
        };
      }
      return {
        [sort.id]: order,
      };
    });

    setSearchParamsLoaded(true);
    setFilters({
      limit,
      offset,
      where: {
        _and: andFilters,
      },
      orderBy: orderByFromParams,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (viewMode === 'grid') {
      table.setColumnVisibility({
        serial: true,
      });
    } else {
      table.setColumnVisibility(DEFAULT_COLUMN_VISIBILITY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  return (
    <ErrorBoundary fallbackRender={ErrorBoundaryFallbackRender}>
      <ListDeviceHeader
        onTimezoneChange={setSelectedTimezone}
        selectedTimezone={selectedTimezone}
        filters={filterFormsData}
        setFilters={setFilterFormsData}
        table={table}
        viewMode={viewMode}
        refetchDevices={refetch}
      />
      <DataTable
        viewMode={viewMode}
        table={table}
        loading={isLoading}
        fetching={isFetching}
      />
    </ErrorBoundary>
  );
}
